import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader, BrutalCard, Chip, EmptyState } from "../components/brutal";
import { Coins, Send, CheckCircle2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const STATUS_ICON = { pending: Clock, approved: CheckCircle2, denied: XCircle };
const STATUS_COLOR = { pending: "bg-brand-yellow", approved: "bg-brand-green", denied: "bg-red-200" };

export default function PayoutPage() {
  const { user, refreshMe } = useAuth();
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/payouts?mine=true").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const pts = parseInt(amount);
    if (!pts || pts <= 0) {
      toast.error("Enter a positive amount");
      return;
    }
    setBusy(true);
    try {
      await api.post("/payouts", { amount_points: pts, note });
      toast.success(`Payout of €${pts} requested — pending admin approval`);
      setAmount("");
      setNote("");
      await load();
      await refreshMe();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const balance = user?.total_points || 0;

  return (
    <div>
      <PageHeader
        title="Request a Payout"
        subtitle="Convert your points into a bonus on your next payroll. 1 point = €1. Your HR admin will review and approve each request."
        right={
          <div className="brutal p-4 bg-brand-yellow flex items-center gap-3">
            <Coins className="w-6 h-6" />
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold">Available balance</div>
              <div className="font-display font-black text-2xl leading-none" data-testid="payout-balance">{balance}</div>
            </div>
          </div>
        }
      />

      <div className="grid lg:grid-cols-3 gap-5">
        <form onSubmit={submit} className="lg:col-span-2">
          <BrutalCard className="p-6" data-testid="payout-form">
            <h2 className="font-display font-black text-2xl mb-5">New payout request</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2">Amount (points)</label>
                <input
                  type="number"
                  min="1"
                  max={balance}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="input-brutal"
                  placeholder="e.g. 100"
                  required
                  data-testid="payout-amount"
                />
                <div className="text-xs text-neutral-600 mt-1">
                  = €{amount || 0} on your payroll
                </div>
              </div>
              <div className="flex items-end">
                <div className="flex flex-wrap gap-2">
                  {[25, 50, 100, 250].filter((v) => v <= balance).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setAmount(String(v))}
                      className="chip bg-white hover:bg-brand-yellow"
                      data-testid={`quick-${v}`}
                    >
                      {v} pts
                    </button>
                  ))}
                  {balance > 0 && (
                    <button
                      type="button"
                      onClick={() => setAmount(String(balance))}
                      className="chip bg-brand-orange text-white"
                      data-testid="quick-max"
                    >
                      Max ({balance})
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-xs font-bold uppercase tracking-widest mb-2">Note (optional)</label>
              <textarea
                rows={3}
                className="input-brutal resize-none"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Anything the admin should know…"
                data-testid="payout-note"
              />
            </div>
            <button
              type="submit"
              disabled={busy || !amount}
              className="btn-brutal btn-primary w-full mt-5 py-3"
              data-testid="payout-submit"
            >
              <Send className="w-4 h-4" /> {busy ? "Submitting…" : `Request €${amount || 0} payout`}
            </button>
            <div className="text-xs text-neutral-500 mt-3">
              Points are held when submitted. If your request is denied they'll be refunded automatically.
            </div>
          </BrutalCard>
        </form>

        <BrutalCard className="p-6 bg-brand-yellow" color="bg-brand-yellow">
          <h3 className="font-display font-black text-xl">How it works</h3>
          <ol className="mt-4 space-y-3 text-sm list-decimal list-inside">
            <li>Request any amount up to your balance.</li>
            <li>Admin reviews your request.</li>
            <li>On approval, €1 per point is added to your next payroll.</li>
            <li>On denial, points are refunded automatically.</li>
          </ol>
        </BrutalCard>
      </div>

      <h2 className="font-display font-black text-3xl mt-10 mb-4">Your request history</h2>
      {items.length === 0 ? (
        <EmptyState title="No requests yet" description="Submit your first payout request above." />
      ) : (
        <>
          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {items.map((r) => {
              const I = STATUS_ICON[r.status] || Clock;
              return (
                <BrutalCard key={r.id} className="p-4" data-testid={`payout-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-display font-black text-2xl leading-none">€{r.amount_points}</div>
                      <div className="text-xs text-neutral-600 mt-1">{r.amount_points} pts</div>
                    </div>
                    <span className={`chip ${STATUS_COLOR[r.status] || "bg-white"}`}>
                      <I className="w-3 h-3" /> {r.status}
                    </span>
                  </div>
                  {r.note && <div className="text-sm text-neutral-700 mt-3">{r.note}</div>}
                  <div className="text-[11px] text-neutral-500 mt-3 flex justify-between">
                    <span>Submitted {new Date(r.created_at).toLocaleDateString()}</span>
                    {r.processed_at && <span>Processed {new Date(r.processed_at).toLocaleDateString()}</span>}
                  </div>
                </BrutalCard>
              );
            })}
          </div>

          {/* Desktop table */}
          <BrutalCard className="p-0 overflow-hidden hidden md:block">
            <table className="w-full text-sm" data-testid="payout-history">
              <thead className="bg-brand-ink text-white">
                <tr>
                  <th className="text-left p-4 font-display font-bold">Amount</th>
                  <th className="text-left p-4 font-display font-bold">Note</th>
                  <th className="text-left p-4 font-display font-bold">Status</th>
                  <th className="text-left p-4 font-display font-bold">Submitted</th>
                  <th className="text-left p-4 font-display font-bold">Processed</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const I = STATUS_ICON[r.status] || Clock;
                  return (
                    <tr key={r.id} className="border-t-2 border-black/10">
                      <td className="p-4">
                        <div className="font-display font-black text-lg">€{r.amount_points}</div>
                        <div className="text-xs text-neutral-600">{r.amount_points} pts</div>
                      </td>
                      <td className="p-4 text-xs text-neutral-700 max-w-xs">{r.note || "—"}</td>
                      <td className="p-4">
                        <span className={`chip ${STATUS_COLOR[r.status] || "bg-white"}`}>
                          <I className="w-3 h-3" /> {r.status}
                        </span>
                      </td>
                      <td className="p-4 text-xs text-neutral-700">{new Date(r.created_at).toLocaleDateString()}</td>
                      <td className="p-4 text-xs text-neutral-700">
                        {r.processed_at ? new Date(r.processed_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </BrutalCard>
        </>
      )}
    </div>
  );
}
